import _ from "lodash";
import React, { useState } from "react";

// Components
import AssessmentIcon from "UIComponents/AssessmentIcon";
import I18nHOC from "UIComponents/I18nHOC";
import MetaTags from "UIComponents/MetaTags";
import UIModal from "UIComponents/UIModal";
import FullScreenLoader from "UIComponents/FullScreenLoader";
import LockedScreenWrapper from "UIComponents/LockedScreenComponents";
import {
  EntitySelectionModal,
  EntitySelectionModalConfig,
} from "AppComponents/EntitySelectionModal";
import AddAssignment from "ClassRoom/components/AddAssignment";
import CoachMarks from "AppComponents/CoachMarks";
import LEIntoUnitImport from "AppComponents/LEIntoUnitImport";
import CrownPremiumFilledWrapper from "ToddleLibrary/components/Icons/CrownPremiumFilledWrapper";

// DS
import {
  IconButton,
  Button,
  Tooltip,
  Dropdown,
  DropdownMenu,
  Dialog,
} from "@toddle-edu/ds-web";
import {
  PrintOutlined,
  ChatOutlined,
  PencilOutlined,
  StudentAddOutlined,
  ChevronLeftOutlined,
  ShareCurveOutlined,
  HeartLikeOutlined,
  BookmarkOutlined,
  HeartLikeFilled,
  CloseOutlined,
  BookmarkColored,
} from "@toddle-edu/ds-icons";

// Utils
import { getCommunityImportOptions, getAssignmentModalData } from "../../Utils";
import { getRethumbUrl } from "Utils";
import {
  findCountryUser,
  isGlobalUser,
} from "routes/routes/Teacher/routes/Courses/modules/utils";
import ConditionalWrapper from "Utils/ConditionalWrapper";
import {
  getBookmarkTooltip,
  getUserName,
} from "ToddleLibrary/modules/ToddleLibraryUtils";
import ACLStore from "lib/aclStore";

// Constants
import {
  ASSESSMENT,
  ASSESSMENT_TYPE_AI_TUTOR,
  TASK_TYPE_AI_TUTOR,
  TASK_TYPE_WORKSHEET,
  TASK_TYPE_QUICK_TASK,
} from "Constants/stringConstants";
import { ASSESSMENT_TYPE_WORKSHEET } from "Worksheet/modules/WorksheetConstants";

// third party
import { compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import { recordEvent } from "lib/eventTracker";
import { goBack } from "services/NavigationService";
import classNames from "classnames";

// Modules
import {
  getCurrentCurriculumProgramType,
  getSelectedAcademicYearId,
  getCurrentAcademicYear,
} from "modules/CommonSelectors";
import { getUserInfo } from "Login/modules/LoginModule/LoginUtils";
import { isUserAuthenticatedForAction } from "Login/modules/LoginModule";
import { getCourseDetailsFromCache } from "Course/modules/CourseGraphqlHelpers";
import { getAssessmentDetailsFromCache } from "Assessments/modules/AssessmentGraphqlHelpers";
import { getOrganizationDetailsFromCache } from "modules/CommonGraphqlHelpers";
import { isMapleBearLikeEducator } from "Courses/modules/utils";
import { checkTermsExistsForCurrentCurriculumProgram } from "modules/Services";
import {
  importAssessment,
  cloneAssessment,
} from "Assessments/modules/AssessmentModule";
import {
  getIsCurrentAcademicYear,
  setCurrentCurriculumProgram,
} from "Platform/modules/PlatformModule";
import { getUnitPlanDetails } from "IBPlanner/modules/IBPlannerModule";
import { updateCoachMarkStatus } from "modules/CoachMarksModule";
import {
  setCurriculumProgram,
  getAssessmentObj,
} from "ClassRoom/modules/ClassRoomModule";

// Scss
import classes from "./Header.scss";

import { getIsLearningCoursesEnabled } from "LearningCourses/modules/Utils";
import { getUnitFlowModuleFuncs } from "UnitFlowV3/modules/UnitFlowModule";
import { checkIfTermExistsForCurrentLearningCourse } from "LearningCourse/modules/Utils";
import { getIsProSchool } from "UnitPlans/modules/Utils";

// Use require() wrapped in a getter to hide AITutorAwardsReviewer from
// madge's cycle detector — its transitive deps reach back through this file's
// upstream chain and would close new cycles vs develop. Same pattern as
// AppComponents/CopyUnitPlanModal/LibraryFeed/LibraryFeed.js.
const getAITutorAwardsReviewer = () =>
  require("AppComponents/AITutorAwards/AITutorAwardsReviewer").default;

const styles = {
  buttonWithCount: {
    padding: "8px",
  },
  theme: {
    containerStyle: {
      gap: "12px",
    },
    actionIconStyles: {
      COMMENT: {
        showCommentAlways: true,
      },
    },
  },
};

const COACHMARK_PARAMS = { target: "#COACHMARKS_COMMUNITY_IMPORT_LE" };

const UnitPlanHeaderRight = props => {
  const {
    t,
    setIsLoading,
    onClickPrint,
    isDemo,
    onCommentClick,
    onClickEdit,
    disableAssignToStudents,
    centralRepoConfig,
    assessmentType,
    taskTypeGroup,
    isEditPermission,
    assessmentLocaleLabel,
    courseId,
    course,
    courseAcademicYearsIds,
    parentId,
    image,
    title,
    cloneAssessment,
    isTermsExist,
    isGradingPeriodUnlocked = true,
    isCommentActive,
    totalCommentCount,
    testId,
  } = props;

  const [clonedAssessmentId, setClonedAssessmentId] = useState(null);
  const isProSchool = getIsProSchool();
  const {
    shouldDisableEdit = false,
    isGlobalOrCountryUser,
  } = centralRepoConfig;

  const showAssignToStudents =
    !disableAssignToStudents &&
    !isDemo &&
    !isGlobalOrCountryUser &&
    getIsCurrentAcademicYear() &&
    !isProSchool;

  const disableAssignToStudentsButton =
    (assessmentType !== "qt" &&
      taskTypeGroup !== TASK_TYPE_QUICK_TASK &&
      !isTermsExist) ||
    !isGradingPeriodUnlocked;

  const assignmentModalData = getAssignmentModalData({
    t,
    type: "ASSIGN_TO_STUDENTS",
  });

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const createAssignment = async () => {
    // In case of single assessment copy do not import assessment
    // assign same assessment in class
    if (ACLStore.can("FeatureFlag:SingleAssessmentCopy")) {
      setClonedAssessmentId(parentId);
      return;
    }

    setIsLoading(true);

    const { clonedAssessmentId } = await cloneAssessment({
      sourceAssessmentId: parentId,
      portalType: "PLANNER",
      academicYears: courseAcademicYearsIds,
    });

    setIsLoading(false);

    if (clonedAssessmentId) {
      setClonedAssessmentId(clonedAssessmentId);
    }
  };

  const isPrintEnabled = !ACLStore.can(
    "FeatureFlag:PreventPrintAndScreenshots"
  );

  const disabledTooltipForAssignButton = isGradingPeriodUnlocked
    ? t(
        "classRoom:assign_to_students_no_grading_periods_tooltip_with_label_plural",
        {
          label: assessmentLocaleLabel,
        }
      )
    : t("academicSetup:you_cannot_with_label_since_locked", {
        label: t("common:assign_to_students"),
      });

  const isAITutor =
    assessmentType === ASSESSMENT_TYPE_AI_TUTOR ||
    taskTypeGroup === TASK_TYPE_AI_TUTOR;
  const AITutorAwardsReviewer = isAITutor ? getAITutorAwardsReviewer() : null;

  return (
    <div className={classes.flexWithGap}>
      {isAITutor ? (
        <AITutorAwardsReviewer
          assessmentId={parentId}
          assessmentType={assessmentType}
        />
      ) : null}

      {/* Print button */}
      {assessmentType !== ASSESSMENT_TYPE_WORKSHEET &&
        taskTypeGroup !== TASK_TYPE_WORKSHEET &&
        isPrintEnabled && (
          <Tooltip
            tooltip={t("common:print")}
            placement={"bottom"}
            showArrow={true}
          >
            <IconButton
              size={"medium"}
              _legacy_variant={"outlined-subtle"}
              variant="neutral"
              type="outlined"
              icon={<PrintOutlined variant="subtle" />}
              onClick={() => onClickPrint()}
              testId={`${testId}-header-print`}
            />
          </Tooltip>
        )}

      {/* Comment Button */}
      {!isDemo ? (
        totalCommentCount > 0 ? (
          <Button
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={<ChatOutlined variant="subtle" />}
            onClick={onCommentClick}
            isActivated={isCommentActive}
            style={styles.buttonWithCount}
            testId={`${testId}-header-chat`}
          >
            {totalCommentCount}
          </Button>
        ) : (
          <IconButton
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={<ChatOutlined variant="subtle" />}
            onClick={onCommentClick}
            isActivated={isCommentActive}
            testId={`${testId}-header-chat`}
          />
        )
      ) : null}

      {/* Edit assessment */}
      {!shouldDisableEdit && !isDemo && !isLearningCoursesEnabled ? (
        <Button
          size={"medium"}
          _legacy_variant={"outlined-subtle"}
          variant="neutral"
          type="outlined"
          icon={<PencilOutlined variant="subtle" />}
          onClick={onClickEdit}
          disabled={
            !isEditPermission &&
            !ACLStore.can("TeacherPortal:ShowLeReadOnly") &&
            !ACLStore.can("TeacherPortal:CanEditUnitPlan")
          }
          testId={`${testId}-header-edit`}
        >
          {t("common:edit")}
        </Button>
      ) : null}

      {/* Assign assessment to students */}
      {showAssignToStudents ? (
        <ConditionalWrapper
          condition={disableAssignToStudentsButton}
          type={"wrapperAsElement"}
          wrapper={
            <Tooltip
              tooltip={disabledTooltipForAssignButton}
              placement={"left"}
              showArrow={true}
            />
          }
        >
          <Button
            size={"medium"}
            variant={"progressive"}
            icon={<StudentAddOutlined />}
            onClick={() => createAssignment()}
            disabled={disableAssignToStudentsButton}
            testId={`${testId}-header-assign`}
          >
            {t("common:assign")}
          </Button>
        </ConditionalWrapper>
      ) : null}

      {!_.isEmpty(clonedAssessmentId) ? (
        <UIModal
          isOpen={true}
          isFullScreen={true}
          modalContent={classes.modalContent}
        >
          <AddAssignment
            sourceAssessmentId={parentId}
            mode={assignmentModalData["mode"]}
            creationMode={assignmentModalData["creationMode"]}
            steps={assignmentModalData["steps"]}
            contentType={assignmentModalData["contentType"]}
            createHeader={assignmentModalData["createHeader"]}
            onClose={() => setClonedAssessmentId(null)}
            portalType={"PLANNER"}
            assignmentDetails={{
              assignmentImage: image,
              assignmentTitle: title ?? t("common:untitled"),
            }}
            showImageTitleInHeader={true}
            clonedAssessmentId={clonedAssessmentId}
            courseId={courseId}
            updateUnitPlanDataInRedux={
              assignmentModalData["updateUnitPlanDataInRedux"]
            }
            course={course}
            isFromUnitPlan={true}
            testId={testId}
          />
        </UIModal>
      ) : null}
    </div>
  );
};

const CommunityHeaderRight = props => {
  const {
    t,
    id,
    title,
    userId,
    parentId,
    goBack,
    importAssessment,
    createUnitFlowResources,
    updateCoachMarkStatus,
    userLoggedIn,
    isUserAuthenticatedForAction,
    // getUnitPlanDetails,
    isTermsExist,
    setIsLoading,
    createdBy,
    entityRatings,
    onCommentClick,
    isToddleLibrary,
    communityComments,
    likes,
    savedBy,
    isAssessmentLoading,
    setCurriculumProgram,
    userName,
    platformUserId,
    subscribeToddleLibraryEvents,
    unSubscribeToddleLibraryEvents,
    setCurrentCurriculumProgram,
    currentAcademicYearId,
    testId,
    isLearningCourseEnabledForOrg = false,
  } = props;

  const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

  const [showUnpublishModal, setShowUnpublishModal] = useState(false);
  const [showImportInUnitModal, setShowImportInUnitModal] = useState(false);
  const [showImportInClassModal, setImportLEInClass] = useState(false);
  const [showLockScreenModal, setLockScreenModal] = useState(false);
  const [lockedScreenKey, setLockedScreenKey] = useState("");

  const assignmentModalData = getAssignmentModalData({ t, type: "COMMUNITY" });
  const importSteps = EntitySelectionModalConfig([
    { key: "GRADE_SELECTION" },
    { key: "UNIT_SELECTION" },
  ]);
  const showUnplishButtton =
    !isToddleLibrary && _.get(createdBy, "id") === userId;
  const communityCommentsCount = _.get(communityComments, "totalCount", 0);

  const actionTypes = isToddleLibrary
    ? ["SAVE"]
    : ["LIKE", "SAVE", "COMMENT", "SHARE"];

  // functions
  const onImportMenuItemClick = async ({ key }) => {
    const hasAccess = await isUserAuthenticatedForAction();
    if (!hasAccess) return;

    switch (key) {
      case "IMPORT_IN_UNIT":
        recordEvent({
          eventName: "Clicked import le",
          eventData: {
            target: "learning experience whole view",
            entity_id: id,
            entity_type: "ASSESSMENT",
            source: "community",
            entity_name: title,
          },
        });
        setShowImportInUnitModal(true);
        break;
      case "ASSIGN_TO_STUDENTS":
        setImportLEInClass(true);

        break;
    }

    if (isToddleLibrary) {
      unSubscribeToddleLibraryEvents();
    }
  };

  const handleSubmit = async (
    { unitId, unitFlowId, courseId, selectedUnitCurriculumProgram },
    selectedUnitCurriculumProgramId
  ) => {
    if (!_.isEmpty(selectedUnitCurriculumProgram)) {
      await setCurriculumProgram(selectedUnitCurriculumProgram);
      await setCurrentCurriculumProgram(selectedUnitCurriculumProgram);
    }
    const unitPlanDetails = await getUnitPlanDetails({
      unitPlanId: unitId,
      courseId,
    });

    const unitPlanAcademicYearsIds = _.map(
      _.get(unitPlanDetails, "data.node.academicYears", []),
      item => item.id
    );
    const itemId = await importAssessment({
      sourceAssessmentId: parentId,
      unitId,
      portalType: "PLANNER",
      eventTarget: "learning experience whole view",
      courseId,
      academicYears: unitPlanAcademicYearsIds,
      userInfoPortalType: "COMMUNITY",
      selectedUnitCurriculumProgramId,
    });

    if (itemId) {
      updateCoachMarkStatus({
        type: "COMMUNITY_IMPORT_LE",
        portalType: "COMMUNITY",
      });

      const resources = [
        {
          label: "",
          itemType: "ASSESSMENT",
          itemId,
        },
      ];

      await createUnitFlowResources({
        resources,
        unitFlowId,
        isImportedInLibrary: true,
        isImportFromUnit: true,
      });

      setShowImportInUnitModal(false);
    }
  };

  const unpublishLE = async () => {
    goBack();
  };

  const closeImportInClassModal = () => {
    setImportLEInClass(false);
    if (isToddleLibrary) {
      subscribeToddleLibraryEvents();
    }
  };

  const renderCustomIcon = params => {
    const { value, isFillIcon, fillColor, onClick } = params || {};

    switch (value) {
      case "COMMENT": {
        return communityCommentsCount > 0 ? (
          <Button
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={<ChatOutlined variant="subtle" />}
            onClick={onCommentClick}
            style={styles.buttonWithCount}
            disabled={!userLoggedIn}
          >
            {communityCommentsCount}
          </Button>
        ) : (
          <IconButton
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={<ChatOutlined variant="subtle" />}
            onClick={onCommentClick}
            disabled={!userLoggedIn}
          />
        );
      }
      case "LIKE": {
        return _.get(likes, "totalCount", 0) > 0 ? (
          <Button
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={
              isFillIcon ? (
                <HeartLikeFilled variant={"critical"} />
              ) : (
                <HeartLikeOutlined />
              )
            }
            onClick={onClick}
            style={styles.buttonWithCount}
            disabled={!userLoggedIn}
          >
            {_.get(likes, "totalCount", 0)}
          </Button>
        ) : (
          <IconButton
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={
              isFillIcon ? (
                <HeartLikeFilled variant={"critical"} />
              ) : (
                <HeartLikeOutlined variant="subtle" />
              )
            }
            onClick={onClick}
            disabled={!userLoggedIn}
          />
        );
      }
      case "SAVE": {
        const toddleLibraryBookmarkTooltip = getBookmarkTooltip({
          t,
          isBookmarked: isFillIcon,
        });

        return (
          <Tooltip
            tooltip={toddleLibraryBookmarkTooltip}
            visible={isToddleLibrary}
            showArrow={true}
          >
            <IconButton
              dsVersion="2.0"
              type="plain"
              size={"medium"}
              variant="neutral"
              icon={isFillIcon ? <BookmarkColored /> : <BookmarkOutlined />}
              onClick={onClick}
              disabled={!userLoggedIn || isAssessmentLoading}
              testId={`${testId}-header-save`}
            />
          </Tooltip>
        );
      }
      case "SHARE": {
        return (
          <IconButton
            size={"medium"}
            _legacy_variant={"outlined-subtle"}
            variant="neutral"
            type="outlined"
            icon={<ShareCurveOutlined variant="subtle" />}
            onClick={onClick}
            disabled={!userLoggedIn}
          />
        );
      }
    }
  };

  const closeImportInUnitModal = () => {
    setShowImportInUnitModal(false);
    subscribeToddleLibraryEvents();
  };

  const importInUnitModal = () => {
    return isToddleLibrary ? (
      <LEIntoUnitImport
        headerText={t("common:add_le_to_unit_2")}
        closeModal={closeImportInUnitModal}
        handleSubmit={handleSubmit}
        academicYears={_.compact([currentAcademicYearId])}
      />
    ) : (
      <EntitySelectionModal
        setEntitySelectionModal={() => setShowImportInUnitModal(false)}
        steps={importSteps}
        handleSubmit={handleSubmit}
        portalType={"PLANNER"}
        isFullScreen={true}
        modalTitle={t("common:add_le_to_unit_2")}
      />
    );
  };

  const handleAssignLeToStudent = async () => {
    setImportLEInClass(true);
  };

  const showUseInClassButton =
    !isLearningCourseEnabledForOrg || isToddleLibrary;

  return (
    <div className={classes.flexWithGap}>
      <CoachMarks
        type={"COMMUNITY_IMPORT_LE"}
        continuous={true}
        params={COACHMARK_PARAMS}
        isTransparentOverlay={true}
        disableScrolling={false}
        disableScrollParentFix={false}
        disableOverlay={true}
        startAfterTime={3200}
      />

      {/* Unpublish button */}
      {showUnplishButtton ? (
        <Button
          _legacy_variant={"outlined-subtle"}
          variant="neutral"
          type="outlined"
          size={"medium"}
          onClick={() => setShowUnpublishModal(true)}
        >
          {t("common:unpublish_this_entity", {
            label: t("community:learning_experience"),
          })}
        </Button>
      ) : null}

      {/* Import in unit and assign to students option */}
      {showUseInClassButton ? (
        <div className={classes.viewActionsSection}>
          {isLearningCoursesEnabled ? (
            <Button
              dsVersion="2.0"
              id="COACHMARKS_COMMUNITY_IMPORT_LE"
              size={"medium"}
              disabled={!userLoggedIn}
              testId={`${testId}-header-useInCourse`}
              shortcut={{ code: "KeyC" }}
              onClick={handleAssignLeToStudent}
            >
              {t("toddleLibrary:use_in_course")}
            </Button>
          ) : (
            <Dropdown
              overlay={
                <DropdownMenu
                  dsVersion="2.0"
                  options={getCommunityImportOptions({
                    t,
                    isTermsExist,
                  })}
                  onClick={onImportMenuItemClick}
                  onShortcutPress={onImportMenuItemClick}
                  testId={`${testId}-header-useInClass`}
                />
              }
              disabled={!userLoggedIn}
            >
              <Button
                dsVersion="2.0"
                id="COACHMARKS_COMMUNITY_IMPORT_LE"
                size={"medium"}
                disabled={!userLoggedIn}
                testId={`${testId}-header-useInClass`}
                shortcut={{ code: "KeyC" }}
              >
                {t("common:use_in_class")}
              </Button>
            </Dropdown>
          )}
        </div>
      ) : null}

      {/* Import in unit modal */}
      {showImportInUnitModal ? importInUnitModal() : null}

      {/* Assign to students modal */}
      {showImportInClassModal ? (
        <UIModal isOpen={true} isFullScreen={true}>
          <AddAssignment
            sourceAssessmentId={parentId}
            mode={assignmentModalData["mode"]}
            creationMode={assignmentModalData["creationMode"]}
            steps={assignmentModalData["steps"]}
            assignmentId={assignmentModalData["id"]}
            contentType={assignmentModalData["contentType"]}
            createHeader={assignmentModalData["createHeader"]}
            onClose={closeImportInClassModal}
            portalType={"PLANNER"}
            updateUnitPlanDataInRedux={
              assignmentModalData["updateUnitPlanDataInRedux"]
            }
          />
        </UIModal>
      ) : null}

      {showLockScreenModal ? (
        <LockedScreenWrapper
          featureKey={lockedScreenKey}
          screenType={"modal"}
          onClose={() => setLockScreenModal(false)}
        />
      ) : null}

      {showUnpublishModal ? (
        <Dialog
          isOpen={true}
          variant={"delete-dialog"}
          title={t("common:unpublish_entity", {
            label: t("community:learning_experience"),
          })}
          message={t("community:unpublish_le_from_community", {
            label: title || t("untitled"),
          })}
          buttonOneText={t("common:agree_cancel")}
          buttonTwoText={t("common:action_agree", {
            action: t("common:unpublish"),
          })}
          onButtonOneClick={() => setShowUnpublishModal(false)}
          onButtonTwoClick={unpublishLE}
          onClose={() => setShowUnpublishModal(false)}
        />
      ) : null}
    </div>
  );
};

const Header = props => {
  const {
    t,
    title,
    image,
    curriculumType,
    assessmentType,
    showBackButton,
    onClose,
    userType,
    showViewActions = true,
    isCommunity,
    isToddleLibrary,
    checkTermsExistsForCurrentCurriculumProgram,
    showPremiumBanner,
    isLibraryPremium,
    getAssessmentObj,
    testId,
    isCourseFlowRoute,
    isUnitFlowRoute,
    learningCourseId,
    academicYearId,
  } = props;

  const [isLoading, setIsLoading] = useState(false);

  const assessmentTypeObj = getAssessmentObj({
    curriculumType,
    assessmentValue: assessmentType,
    contentType: ASSESSMENT,
  });

  const { typeLabel, typeLabel2 } = assessmentTypeObj || {};

  const assessmentLocaleLabel = isToddleLibrary
    ? title
    : t(typeLabel || typeLabel2);

  const rethumpUrl = `url(${getRethumbUrl({
    width: 720,
    height: 480,
    imageUrl: image,
    fitIn: true,
  })})`;

  const showActions =
    _.isEqual(userType, "staff") &&
    showViewActions &&
    !isCommunity &&
    !isToddleLibrary &&
    (!getIsLearningCoursesEnabled() || !(isCourseFlowRoute || isUnitFlowRoute));
  const isTermsExist = getIsLearningCoursesEnabled()
    ? checkIfTermExistsForCurrentLearningCourse({
        learningCourseId,
        academicYearId,
      })
    : checkTermsExistsForCurrentCurriculumProgram();

  const assessmentLocaleClass = classNames(
    "heading-5",
    classes.assessmentLocale
  );

  const shouldRenderBackButton = showBackButton && !isToddleLibrary;
  const showTitleTooltip = isToddleLibrary;
  const showTypeLabelAvatar = !isToddleLibrary;

  const containerClass = classNames(classes.container, {
    [classes.customContainerPadding]: isToddleLibrary,
  });

  return (
    <div className={containerClass}>
      <MetaTags image={rethumpUrl} title={title} />
      {/* Back Button */}
      <div className={classes.leftContainer}>
        {shouldRenderBackButton ? (
          <IconButton
            size={"medium"}
            _legacy_variant={"plain"}
            variant="neutral"
            type="plain"
            icon={<ChevronLeftOutlined variant="subtle" />}
            testId={`${testId}-header-back`}
            onClick={onClose}
          />
        ) : null}

        <div className={classes.flexWithGap}>
          {showTypeLabelAvatar ? (
            <AssessmentIcon assessmentType={assessmentType} />
          ) : null}

          {isLibraryPremium ? <CrownPremiumFilledWrapper /> : null}

          <ConditionalWrapper
            condition={showTitleTooltip}
            type={"wrapperAsElement"}
            wrapper={
              <Tooltip tooltip={title} placement={"bottom"} showArrow={true} />
            }
          >
            <div className={assessmentLocaleClass}>{assessmentLocaleLabel}</div>
          </ConditionalWrapper>
        </div>
      </div>

      {/* Right container */}
      {showActions ? (
        <UnitPlanHeaderRight
          {...props}
          isLoading={isLoading}
          setIsLoading={setIsLoading}
          isTermsExist={isTermsExist}
        />
      ) : null}

      {isCommunity || isToddleLibrary ? (
        <div className={classes.communityHeaderContainer}>
          {isCommunity || !showPremiumBanner ? (
            <CommunityHeaderRight
              {...props}
              isLoading={isLoading}
              setIsLoading={setIsLoading}
              isTermsExist={isTermsExist}
            />
          ) : null}

          {/* Close button */}
          {isToddleLibrary ? (
            <IconButton
              dsVersion="2.0"
              variant="neutral"
              type="plain"
              icon={<CloseOutlined variant="subtle" />}
              onClick={onClose}
              testId={`${testId}-header-close`}
            />
          ) : null}
        </div>
      ) : null}

      {/* Loader */}
      {isLoading ? <FullScreenLoader /> : null}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const { fields, isToddleLibrary } = ownProps;
  const curriculumType = getCurrentCurriculumProgramType(state);

  const title = _.get(_.find(fields, { uid: "title" }), "value", "");

  const userInfo = getUserInfo();
  const courseId = state.teacher.selected_class.selected_course;
  const disableAssignToStudents = !_.get(ownProps, "params.course_id", null);

  const platformUserInfo = getUserInfo({ portalType: "PLANNER" });
  const platformUserId = _.get(platformUserInfo, "id", null);
  const platformOrgId = _.get(platformUserInfo, "org_id", null);
  const userName = getUserName(state);

  const academicYearId = getSelectedAcademicYearId(state);

  const courseDetails = getCourseDetailsFromCache({
    id: courseId,
    courseGradeFilters: { academicYearId },
  });

  const { params: { assessmentId = null } = {} } = ownProps;

  const collaboratorsFilter = {
    userAcademicYearId: academicYearId,
  };

  const assessmentDetails = getAssessmentDetailsFromCache({
    id: assessmentId,
    collaboratorsFilter,
  });
  const { isImported = false } = assessmentDetails;

  const organizationId = userInfo.org_id;
  const organizationDetails = getOrganizationDetailsFromCache(organizationId);
  const organizationTags = _.get(organizationDetails, "tags", []);
  const isEducator = isMapleBearLikeEducator({ organizationTags });
  const shouldDisableActionsForEducator = isImported && isEducator;
  const userTags = userInfo.tags;
  const isGlobalOrCountryUser = Boolean(
    isGlobalUser({ userTags }) || findCountryUser({ userTags })
  );

  const centralRepoConfig = {
    shouldDisableEdit: shouldDisableActionsForEducator,
    shouldDisableDelete: shouldDisableActionsForEducator,
    isGlobalOrCountryUser: isGlobalOrCountryUser,
  };

  const courseAcademicYearsIds = _.map(
    _.get(courseDetails, "academicYears", []),
    item => item.id
  );

  const currentAcademicYear = getCurrentAcademicYear({
    organizationId: platformOrgId,
  });
  const currentAcademicYearId = _.get(currentAcademicYear, "id");

  let isLearningCourseEnabledForOrg = false;

  if (isToddleLibrary) {
    isLearningCourseEnabledForOrg = _.get(
      state,
      "toddleLibrary.isLearningCourseEnabledForOrg",
      false
    );
  }

  const isCourseFlowRoute =
    location.href.search("course-flow") > -1 ||
    location.href.search("course-plan") > -1;

  return {
    curriculumType,
    title,
    centralRepoConfig,
    course: courseDetails,
    courseId,
    courseAcademicYearsIds,
    disableAssignToStudents,
    userId: userInfo.id,
    userLoggedIn: _.get(state, "login.userLoggedIn"),
    userName,
    platformUserId,
    currentAcademicYearId,
    isCourseFlowRoute,
    isUnitFlowRoute: location.href.search("unitFlow") > -1,
    isLearningCourseEnabledForOrg,
    academicYearId,
  };
};

const mapActionCreators = () => {
  const { createUnitFlowResources } = getUnitFlowModuleFuncs();

  return {
    importAssessment,
    createUnitFlowResources,
    goBack,
    updateCoachMarkStatus,
    isUserAuthenticatedForAction,
    cloneAssessment,
    getIsCurrentAcademicYear,
    getUnitPlanDetails,
    checkTermsExistsForCurrentCurriculumProgram,
    setCurriculumProgram,
    getAssessmentObj,
    setCurrentCurriculumProgram,
  };
};

export default compose(
  withRouter,
  connect(mapStateToProps, mapActionCreators()),
  I18nHOC({ resource: ["common", "community", "classRoom"] })
)(Header);
